import get from 'lodash/get';
import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import partialRight from 'lodash/partialRight';
import React, { useCallback, useEffect, useState } from 'react';
import apiConstants from '~/locales/internal/api.json';
import { DEFAULT_NAMESPACE, DEFAULT_LOCALE, DEFAULT_LOCALE_WITH_REGION } from './constants';

// NOTE: Using something like Webpack's EnvironmentPlugin we could even set the
// default locale here via process.env.DEFAULT_LOCALE
/** @returns {Promise} */
export async function initializeI18n(locale = DEFAULT_LOCALE) {
  return i18n.use(initReactI18next).init({
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    lng: locale,
    resources: {
      [DEFAULT_LOCALE]: {
        [DEFAULT_NAMESPACE]: {
          ...apiConstants,
        },
      },
    },
  });
}

/** Helper for loading i18n resources, primarily used with the useI18n hook but
 *  useful by itself for loading bundles before jest tests run or in class
 *  components (where it seems the consensus is to load in the constructor)
 *
 *  The way this works is that i18next will check if the translation at the
 *  provided `translationKey` exists and if not will load the [`language`].json
 *  file at the given `importPath` relative to `~/locales`
 *
 *  @param {string} options.importPath - path to the json we want to load copy
 *  from, this should be a path relative to '~/locales', i.e. if you want to
 *  load: '~/locales/whatever/something/en.json',
 *  then you would do { importPath: 'whatever/something' }
 *  @param {string} options.language - a locale string like 'en', 'de', ...
 *  @param {string} options.resource - the name of the file to load if it is not
 *  named after the current locale, e.g. en.json, de.json, ...
 *  @param {string} options.translationKey - a string like 'features.myFeature'
 *  that will be used to check if the relevant copy has already been loaded
 *  @param {string} options.namespace - See the i18next docs for namespaces
 */
export async function loadResourceBundle(options) {
  const {
    importPath,
    language = DEFAULT_LOCALE,
    resource = '',
    translationKey,
    namespace = DEFAULT_NAMESPACE,
  } = options;

  if (!!importPath && !!translationKey && !i18n.exists(translationKey)) {
    const resolvedLanguage = language || i18n.language || DEFAULT_LOCALE;
    const fileName = `${resource || resolvedLanguage}`;
    // In order to get webpack to lazy-load properly we have to hardcode
    // the start of the path
    const resourceName = `../../locales/${importPath}/${fileName}.json`;
    const resourceBundles = import.meta.glob('../../locales/**/*.json');

    if (!(resourceName in resourceBundles)) {
      throw new Error(`Cannot load i18n resource - ${resourceName}`);
    }

    const resourceBundle = await resourceBundles[resourceName]();


    i18n.addResourceBundle(
      resolvedLanguage,
      namespace,
      resourceBundle,
      true,
      true,
    );
  }
}

/** Hook to assist components in loading their relevant copy and other strings
 *  We use a Promise in the useEffect to ensure that we only call and remove the
 *  event listeners once
 *  @param {Object} options - See docs for loadResouceBundle for info
 */
export function useI18n(options) {
  const { translationKey } = options;
  const [isLoading, setIsLoading] = useState(!i18n.exists(translationKey));
  const { t } = useTranslation();
  const subscription = useCallback((_language, _namespace, resources, resolve) => {
    if (
      isLoading
        && get(resources, translationKey)
        && i18n.exists(translationKey)
    ) {
      setIsLoading(false);
      resolve();
    }
  }, [isLoading, translationKey]);

  useEffect(() => {
    if (isLoading) {
      let listener;

      (new Promise(resolve => {
        listener = partialRight(subscription, resolve);

        i18n.store.on('added', listener);
        loadResourceBundle(options);
      })).then(() => {
        i18n.store.off('added', listener);
      });
    }
  }, [translationKey, isLoading, options, subscription]);

  return { isLoading, t };
}

/** HOC for loading translations specific to a component that also provides the
 *  `t()` function from i18next as a prop
 *  Given a translation file that lives at ~/locales/test/en.json with the JSON
 *  ```json
 *  { "test": { "test": "test" } }
 *  ```
 *  You can load this translation for your component by doing
 *  ```javascript
 *  export default withI18n({
 *    importPath: 'test', translationKey: 'test.test',
 *  })(MyComponent);
 *  ```
 *  And calling `t('test.test')` in your component will yield the string "test"
 *  @param {Object} options - See docs for `loadResouceBundle` for info
 */
export const withI18n = options => Component => props => {
  const { isLoading, t } = useI18n(options);

  if (isLoading) {
    return null;
  }

  return (
    <Component {...props} t={t} />
  );
};

// Based on some criteria, get the BCP 47 language tag (i.e. locale). Right now, we attempt to read the language from the browser itself - which is likely a
//  short term solution. In the future, the language tag/locale should be included in the user context and may be configured on a customer or agent level.
//  Different browsers allow different configurations and some even inherit this data from the client OS. For more info, see the stackoverflow link
//  below =>
//  https://stackoverflow.com/questions/25606730/get-current-locale-of-chrome
export const getLocale = () => {
  let locale = DEFAULT_LOCALE_WITH_REGION;
  if (window.navigator.languages && window.navigator.languages.length > 0) {
    const [primaryLanguageWithRegion] = window.navigator.languages;
    locale = primaryLanguageWithRegion;
  } else {
    locale = window.navigator.userLanguage
      || window.navigator.language
      || DEFAULT_LOCALE_WITH_REGION;
  }
  return locale;
};
